// Site search. The index (search-index.json) is generated at build time by SearchIndexPlugin
// in webpack.common.js and only fetched the first time the search box is opened.

const INDEX_URL = '/search-index.json';
const MAX_RESULTS = 8;
const SUGGESTIONS = [
    'esd-products.html',
    'peek-sheets-and-rods.html',
    'ptfe-sheets-and-rods.html',
    'pom-sheets-and-rods.html',
    'pet-polyethylene-terephthalate-sheets.html',
    'cnc-router-job.html',
];

const modalHtml = `
<div id="search-modal" class="fixed inset-0 z-[200] hidden bg-[#0f172a]/50 backdrop-blur-sm px-4 pt-[10vh] pb-6"
    role="dialog" aria-modal="true" aria-label="Search products">
    <div class="mx-auto w-full max-w-[640px] bg-white rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh]">
        <div class="flex items-center gap-3 px-5 border-b border-slate-100">
            <i data-lucide="search" class="w-5 h-5 text-[#718096] shrink-0"></i>
            <input id="search-input" type="search" autocomplete="off" spellcheck="false"
                placeholder="Search materials, e.g. PEEK, ESD, food grade, gears"
                aria-label="Search materials" aria-controls="search-results"
                class="flex-1 min-w-0 py-5 text-[16px] text-[#1a1a1a] placeholder:text-slate-400 bg-transparent focus:outline-none">
            <button type="button" data-search-close aria-label="Close search"
                class="shrink-0 text-[12px] font-semibold text-[#718096] border border-slate-200 rounded px-2 py-1 hover:text-[#1a1a1a] hover:border-slate-300 transition-colors"><span class="sm:hidden">Close</span><span class="hidden sm:inline">Esc</span></button>
        </div>
        <div class="overflow-y-auto">
            <p id="search-heading" class="px-5 pt-4 pb-2 text-[12px] font-semibold uppercase tracking-wider text-[#718096]"></p>
            <ul id="search-results" role="listbox" class="pb-3"></ul>
        </div>
    </div>
</div>`;

const escapeHtml = (s) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const normalise = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
// "pa 6" / "pa6", "g 10" / "g10", "anti static" / "antistatic" should all match
const squash = (s) => s.replace(/ /g, '');

const prepare = (entries) => entries.map((entry) => {
    const title = normalise(entry.title);
    const meta = normalise(`${entry.keywords} ${entry.description}`);
    const text = normalise(entry.text);
    return { ...entry, fields: { title, titleSquashed: squash(title), meta, metaSquashed: squash(meta), text } };
});

const scoreEntry = (entry, terms) => {
    const { title, titleSquashed, meta, metaSquashed, text } = entry.fields;
    let score = 0;
    for (const term of terms) {
        const wordStart = new RegExp(`(^| )${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`);
        if (wordStart.test(title)) score += 10;
        else if (titleSquashed.includes(term)) score += 7;
        else if (wordStart.test(meta)) score += 5;
        else if (metaSquashed.includes(term)) score += 3;
        else if (text.includes(term)) score += 1;
        else return 0; // every word must match somewhere
    }
    if (title.startsWith(terms[0])) score += 3;
    if (entry.type !== 'Page') score += 0.5;
    return score;
};

const search = (entries, query) => {
    const q = normalise(query);
    if (!q) return [];
    const terms = q.split(' ');
    return entries
        .map((entry) => ({ entry, score: scoreEntry(entry, terms) }))
        .filter((r) => r.score > 0)
        // On a tie the shorter title is the closer match ("ESD Products" before "ESD / Antistatic ABS ...")
        .sort((a, b) => b.score - a.score || a.entry.title.length - b.entry.title.length)
        .slice(0, MAX_RESULTS)
        .map((r) => r.entry);
};

const resultHtml = (entry, i) => `
    <li role="option" id="search-option-${i}" aria-selected="false">
        <a href="${escapeHtml(entry.url)}" data-search-result
            class="flex items-start gap-4 px-5 py-3 hover:bg-[#F0F9FF] aria-selected:bg-[#F0F9FF] transition-colors">
            <span class="min-w-0 flex-1">
                <span class="block text-[15px] font-semibold text-[#1a1a1a] truncate">${escapeHtml(entry.title)}</span>
                <span class="block text-[13px] text-[#718096] leading-snug line-clamp-2">${escapeHtml(entry.description)}</span>
            </span>
            <span class="shrink-0 mt-0.5 text-[11px] font-semibold text-primary bg-primary-light rounded px-2 py-0.5">${escapeHtml(entry.type)}</span>
        </a>
    </li>`;

const initSearch = () => {
    const openers = document.querySelectorAll('[data-search-open]');
    if (!openers.length) return;

    document.body.insertAdjacentHTML('beforeend', modalHtml);
    const modal = document.getElementById('search-modal');
    const input = document.getElementById('search-input');
    const list = document.getElementById('search-results');
    const heading = document.getElementById('search-heading');
    if (window.lucide) window.lucide.createIcons();

    let entries = null;
    let loading = null;
    let active = -1;
    let lastFocus = null;

    const load = () => {
        if (!loading) {
            loading = fetch(INDEX_URL)
                .then((res) => {
                    if (!res.ok) throw new Error(res.status);
                    return res.json();
                })
                .then((data) => { entries = prepare(data); })
                .catch(() => { loading = null; });
        }
        return loading;
    };

    const options = () => [...list.querySelectorAll('[role="option"]')];
    const setActive = (i) => {
        const opts = options();
        if (!opts.length) { active = -1; return; }
        active = (i + opts.length) % opts.length;
        opts.forEach((opt, n) => {
            const selected = n === active;
            opt.setAttribute('aria-selected', String(selected));
            opt.querySelector('a').toggleAttribute('aria-selected', selected);
        });
        input.setAttribute('aria-activedescendant', opts[active].id);
        opts[active].scrollIntoView({ block: 'nearest' });
    };

    const render = () => {
        const query = input.value.trim();
        active = -1;
        input.removeAttribute('aria-activedescendant');

        if (!entries) {
            heading.textContent = 'Loading…';
            list.innerHTML = '';
            return;
        }
        let results;
        if (!query) {
            heading.textContent = 'Popular';
            results = SUGGESTIONS.map((url) => entries.find((e) => e.url === url)).filter(Boolean);
        } else {
            results = search(entries, query);
            heading.textContent = results.length ? 'Results' : '';
        }
        if (!results.length && query) {
            list.innerHTML = `
                <li class="px-5 pb-4 text-[14px] text-[#4A5568] leading-relaxed">
                    No materials match "<span class="font-semibold text-[#1a1a1a]">${escapeHtml(query)}</span>".
                    We stock more than we list online, so
                    <a href="contact.html" class="font-semibold text-primary hover:underline">send us an enquiry</a>
                    or call <a href="tel:+917200567899" class="font-semibold text-primary hover:underline">+91 7200567899</a>.
                </li>`;
            return;
        }
        list.innerHTML = results.map(resultHtml).join('');
    };

    const open = () => {
        lastFocus = document.activeElement;
        modal.classList.remove('hidden');
        document.body.classList.add('overflow-hidden');
        input.value = '';
        render();
        input.focus();
        load().then(render);
    };

    const close = () => {
        modal.classList.add('hidden');
        document.body.classList.remove('overflow-hidden');
        if (lastFocus) lastFocus.focus();
    };

    openers.forEach((btn) => btn.addEventListener('click', open));
    modal.addEventListener('click', (e) => {
        if (e.target === modal || e.target.closest('[data-search-close]')) close();
    });
    input.addEventListener('input', render);
    input.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); setActive(active + 1); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(active - 1); }
        else if (e.key === 'Enter') {
            const opts = options();
            const target = opts[active] || opts[0];
            if (target) { e.preventDefault(); target.querySelector('a').click(); }
        }
    });

    document.addEventListener('keydown', (e) => {
        const typing = /^(INPUT|TEXTAREA|SELECT)$/.test(document.activeElement?.tagName) || document.activeElement?.isContentEditable;
        if (!modal.classList.contains('hidden')) {
            if (e.key === 'Escape') close();
            return;
        }
        if ((e.key === '/' && !typing) || (e.key.toLowerCase() === 'k' && (e.ctrlKey || e.metaKey))) {
            e.preventDefault();
            open();
        }
    });
};

export default initSearch;
